import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { Box, Inline, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import messages from './messages';

const styles = cssMap({
	cardContainer: {
		width: '100%',
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.raised'),
		marginTop: token('space.200'),
		marginBottom: token('space.200'),
	},
	header: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	content: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		width: '100%',
	},
	iconWrapper: {
		marginLeft: token('space.200'),
		width: '24px',
		height: '24px',
		borderRadius: token('radius.small'),
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		backgroundColor: token('color.background.neutral.subtle'),

		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	subSection: {
		marginLeft: token('space.200'),
		display: 'flex',
		flex: 1,
		width: '300px',
	},
});

type Props = {
	children: React.ReactNode;
	defaultExpanded?: boolean;
	header: React.ReactNode;
	subSection?: React.ReactNode;
} & (
	| { expandableSectionName: string; isExpandable: boolean }
	| { expandableSectionName?: never; isExpandable?: false }
);

export const Card = ({
	header,
	children,
	subSection,
	isExpandable,
	expandableSectionName,
	defaultExpanded,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(!!defaultExpanded);

	const handleToggle = useCallback(() => {
		setIsExpanded((prev) => !prev);
	}, []);

	const Icon = isExpanded ? ChevronUpIcon : ChevronDownIcon;
	const expandButtonLabel = formatMessage(
		isExpanded ? messages.collapseButtonWithLabel : messages.expandButtonWithLabel,
		{ label: expandableSectionName },
	);

	return (
		<Box xcss={styles.cardContainer}>
			<Inline xcss={styles.header} alignBlock="center" spread="space-between">
				<Box>{header}</Box>
				<Inline alignBlock="center" alignInline="center" spread="space-between">
					{subSection && !isExpanded && <Box xcss={styles.subSection}>{subSection}</Box>}
					{isExpandable && (
						<Pressable xcss={styles.iconWrapper} onClick={handleToggle}>
							<Icon
								spacing="spacious"
								color={token('color.text')}
								size="small"
								label={expandButtonLabel}
							/>
						</Pressable>
					)}
				</Inline>
			</Inline>
			{isExpanded ? <Box xcss={styles.content}>{children}</Box> : null}
		</Box>
	);
};
